import { Component, ReactNode } from 'react'
import { Box, Typography, Button, Alert, Paper } from '@mui/material'
import { Refresh as RefreshIcon } from '@mui/icons-material'
import { useT } from '@/i18n/useT'

interface Props {
  children: ReactNode
}

/**
 * The crash screen is its own function component so it can use the translation
 * hook — the boundary itself has to stay a class. If the crash took the
 * LanguageProvider with it, useT() falls back to the English source strings
 * rather than throwing a second time.
 */
function ErrorFallback({ message }: { message?: string }) {
  const { t } = useT()
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        p: 2,
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 600 }}>
        <Typography variant="h5" gutterBottom color="error">
          {t('Something went wrong')}
        </Typography>
        <Alert severity="error" sx={{ mb: 3 }}>
          {message || t('Unknown error')}
        </Alert>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          {t(
            'Please reload the page. If the problem persists, check your Excel file for invalid data or formats.',
          )}
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={() => window.location.reload()}
        >
          {t('Reload')}
        </Button>
      </Paper>
    </Box>
  )
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: { componentStack: string }) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback message={this.state.error?.message} />
    }

    return this.props.children
  }
}
